import { useEffect, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import api, { formatError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Trash2, Plus, Printer, Palette, Wrench, BarChart3 } from "lucide-react";
import { Link } from "react-router-dom";

const PALETTE = [
  "#0f2b6b", "#C9A227", "#059669", "#dc2626",
  "#7c3aed", "#0891b2", "#ea580c", "#db2777",
  "#0284c7", "#65a30d", "#a16207", "#4b5563",
];

function ColorSwatch({ value, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {PALETTE.map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => onChange(c)}
          className={`w-6 h-6 rounded-full ring-2 transition-all ${
            value === c ? "ring-foreground ring-offset-2 ring-offset-background scale-110" : "ring-transparent"
          }`}
          style={{ backgroundColor: c }}
          aria-label={`Choose color ${c}`}
        />
      ))}
    </div>
  );
}

export default function TechniciansPage() {
  const [techs, setTechs] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [color, setColor] = useState(PALETTE[2]);

  const load = async () => {
    try {
      const { data } = await api.get("/technicians");
      setTechs(data);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await api.post("/technicians", { name, email: email || null, phone: phone || null, color });
      setName(""); setEmail(""); setPhone(""); setColor(PALETTE[2]);
      toast.success("Technician added");
      await load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  const updateColor = async (id, c) => {
    try { await api.patch(`/technicians/${id}`, { color: c }); await load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/technicians/${id}`); toast.success("Technician removed"); await load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <DashboardShell>
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <div className="font-mono-label text-muted-foreground">Team</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">Service Technicians</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            Assign each technician a color chip so you can spot who inspected which vehicle on the MPI, appointment list, and reports.
          </p>
        </div>
        <Button variant="outline" onClick={() => window.print()} className="no-print" data-testid="print-techs-btn">
          <Printer className="w-4 h-4 mr-2" strokeWidth={1.75} />
          Print roster
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="p-5 border-border lg:col-span-2 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead className="w-52">
                  <div className="flex items-center gap-1.5">
                    <Palette className="w-3.5 h-3.5" strokeWidth={2} />
                    <span>Color</span>
                  </div>
                </TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {techs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8">
                    <Wrench className="inline w-4 h-4 mr-2" strokeWidth={1.75} />
                    No technicians yet. Add one on the right.
                  </TableCell>
                </TableRow>
              )}
              {techs.map((t) => (
                <TableRow key={t.id} data-testid={`technician-${t.id}`}>
                  <TableCell>
                    <div
                      className="w-3.5 h-3.5 rounded-full ring-2 ring-background shadow"
                      style={{ backgroundColor: t.color || "#0f2b6b" }}
                      data-testid={`tech-color-dot-${t.id}`}
                    />
                  </TableCell>
                  <TableCell className="font-medium">{t.name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{t.email || "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{t.phone || "—"}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {PALETTE.slice(0, 8).map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => updateColor(t.id, c)}
                          className={`w-4 h-4 rounded-full ring-2 transition-all ${
                            (t.color || "#0f2b6b") === c
                              ? "ring-foreground ring-offset-1 ring-offset-background"
                              : "ring-transparent hover:ring-muted-foreground/40"
                          }`}
                          style={{ backgroundColor: c }}
                          aria-label={`Change ${t.name} color`}
                        />
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Link to={`/technician-scorecard/${t.id}`} data-testid={`tscorecard-link-${t.id}`}>
                        <Button size="sm" variant="outline" title="View scorecard">
                          <BarChart3 className="w-4 h-4" strokeWidth={1.75} />
                        </Button>
                      </Link>
                      <Button size="sm" variant="ghost" onClick={() => remove(t.id)} data-testid={`delete-tech-${t.id}`}>
                        <Trash2 className="w-4 h-4" strokeWidth={1.75} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        <Card className="p-5 border-border">
          <div className="font-mono-label text-muted-foreground">New Technician</div>
          <h2 className="font-display text-lg font-bold tracking-tight mt-1 mb-4">Add to team</h2>
          <form onSubmit={add} className="space-y-3" data-testid="add-tech-form">
            <div>
              <Label htmlFor="t-name" className="font-mono-label">Name</Label>
              <Input id="t-name" value={name} onChange={(e) => setName(e.target.value)} required data-testid="tech-name-input" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="t-email" className="font-mono-label">Email</Label>
              <Input id="t-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="tech-email-input" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="t-phone" className="font-mono-label">Phone</Label>
              <Input id="t-phone" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="tech-phone-input" className="mt-1.5" />
            </div>
            <div>
              <Label className="font-mono-label">Color</Label>
              <div className="mt-2 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full ring-2 ring-foreground/20 shadow" style={{ backgroundColor: color }} />
                <ColorSwatch value={color} onChange={setColor} testid="new-tech-palette" />
              </div>
            </div>
            <Button type="submit" className="w-full" data-testid="add-tech-submit">
              <Plus className="w-4 h-4 mr-2" strokeWidth={2} />
              Add technician
            </Button>
          </form>
        </Card>
      </div>
    </DashboardShell>
  );
}
